import { useState } from "react";
import { TAB_COLOR } from "../constants";
import { toN, parseTSV, isMonthLike } from "../utils";

export function NavTab({label, active, onClick, badge, tabId}) {
  const accent = TAB_COLOR[tabId] || "#2D7A2D";
  return (
    <button onClick={onClick} style={{
      padding:"10px 18px",
      background:active?`${accent}18`:"transparent",
      border:"none",
      borderBottom:active?`3px solid ${accent}`:"3px solid transparent",
      color:active?accent:"#999",
      fontWeight:active?"800":"400",
      cursor:"pointer", fontSize:"13px", fontFamily:"'Noto Sans JP',sans-serif",
      display:"flex", alignItems:"center", gap:"6px", whiteSpace:"nowrap",
      transition:"all 0.15s",
    }}>
      {label}
      {badge && <span style={{background:accent,color:"#fff",borderRadius:"999px",fontSize:"10px",padding:"1px 7px",fontWeight:"700"}}>{badge}</span>}
    </button>
  );
}

export function DropdownNavTab({label, active, subActive, onClick, onSubClick, subItems, badge, tabId}) {
  const [hover, setHover] = useState(false);
  const isAnyActive = active || !!subActive;
  const accent = TAB_COLOR[tabId] || TAB_COLOR[subActive] || "#C0392B";
  return (
    <div style={{position:"relative"}} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}>
      <button onClick={onClick} style={{
        padding:"10px 18px",
        background:isAnyActive?`${accent}18`:"transparent",
        border:"none",
        borderBottom:isAnyActive?`3px solid ${accent}`:"3px solid transparent",
        color:isAnyActive?accent:"#999",
        fontWeight:isAnyActive?"800":"400",
        cursor:"pointer", fontSize:"13px", fontFamily:"'Noto Sans JP',sans-serif",
        display:"flex", alignItems:"center", gap:"4px", whiteSpace:"nowrap",
        transition:"all 0.15s",
      }}>
        {label}
        {badge && <span style={{background:accent,color:"#fff",borderRadius:"999px",fontSize:"10px",padding:"1px 7px",fontWeight:"700"}}>{badge}</span>}
        <span style={{fontSize:"9px",color:isAnyActive?accent:"#ccc",marginLeft:"2px"}}>▾</span>
      </button>
      {hover && (
        <div style={{
          position:"absolute",top:"100%",left:0,zIndex:1000,
          background:"#fff",border:`1px solid ${accent}44`,borderRadius:"8px",
          boxShadow:"0 6px 20px rgba(0,0,0,0.12)",minWidth:"140px",padding:"4px 0",
        }}>
          {subItems.map(item=>{
            const subAccent = TAB_COLOR[item.id] || accent;
            return (
              <button key={item.id} onClick={()=>{onSubClick(item.id);setHover(false);}} style={{
                display:"block",width:"100%",textAlign:"left",
                padding:"9px 16px",background:subActive===item.id?`${subAccent}14`:"transparent",
                border:"none",color:subActive===item.id?subAccent:"#555",
                fontWeight:subActive===item.id?"700":"400",
                cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",
                whiteSpace:"nowrap",
              }}>
                {subActive===item.id && <span style={{marginRight:"6px",fontSize:"10px"}}>●</span>}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Card({children, style}) {
  return <div style={{background:"#fff",borderRadius:"8px",border:"1px solid #E8EDE8",padding:"20px",...style}}>{children}</div>;
}

export function ST({children}) {
  return <div style={{fontSize:"11px",fontWeight:"700",color:"#2D7A2D",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>{children}</div>;
}

export function NI({value, onChange, style, w="88px"}) {
  const isEmpty = value===""||value==null;
  return (
    <input type="text"
      value={isEmpty ? "" : String(value)}
      placeholder="—"
      onChange={e=>onChange(e.target.value.replace(/[^0-9]/g,""))}
      style={{
        width:w, padding:"4px 5px", border:"1px solid #D0D8D0", borderRadius:"4px",
        textAlign:"right", fontSize:"12px", fontFamily:"monospace",
        background: isEmpty ? "#FAFCFA" : "#fff",
        color: (value===0||value==="0") ? "#bbb" : "inherit",
        ...style
      }}
    />
  );
}

export function BulkBtn({onSet}) {
  const [open,setOpen] = useState(false);
  const [val,setVal]   = useState("");
  if (!open) return (
    <button onClick={()=>setOpen(true)} style={{fontSize:"10px",padding:"3px 6px",background:"#E8F4E8",border:"1px solid #C8E6C8",borderRadius:"3px",cursor:"pointer",color:"#2D7A2D",whiteSpace:"nowrap"}}>一括</button>
  );
  return (
    <div style={{display:"flex",gap:"2px",alignItems:"center"}}>
      <input autoFocus type="text" value={val} onChange={e=>setVal(e.target.value.replace(/[^0-9]/g,""))}
        style={{width:"58px",fontSize:"11px",padding:"2px 4px",border:"1px solid #2D7A2D",borderRadius:"3px"}}
        onKeyDown={e=>{if(e.key==="Enter"){onSet(val);setOpen(false);setVal("");}if(e.key==="Escape")setOpen(false);}}
      />
      <button onClick={()=>{onSet(val);setOpen(false);setVal("");}}
        style={{fontSize:"10px",padding:"3px 4px",background:"#2D7A2D",color:"#fff",border:"none",borderRadius:"3px",cursor:"pointer"}}>✓</button>
    </div>
  );
}

export function PastePanel({mode, onApply, months}) {
  const [text,setText]       = useState("");
  const [preview,setPreview] = useState(null);
  const [error,setError]     = useState("");
  const hints = {
    revenue_row:"「案件名|5月|6月|…」の行をコピペ（名前列なしもOK）",
    expense_row:"「科目名|5月|6月|…」の行をコピペ",
    actuals_col:"「売上|経費」の2列×12行、または「売上12ヶ月行 + 経費12ヶ月行」の2行形式をコピペ",
    person_row: "「氏名|5月|6月|…」の行をコピペ",
  };
  const mc = months.length;
  const parse = () => {
    setError("");
    try {
      const rows = parseTSV(text).filter(r=>r.some(c=>c!==""));
      if (!rows.length) { setError("データが空です"); return; }
      if (mode !== "actuals_col") {
        const parsed = rows.filter(r=>r.some(c=>c!=="")).map(r => {
          let label = "";
          let nums = r;
          if (r.length === mc + 1) { label = r[0]; nums = r.slice(1); }
          else if (r.length === mc) { nums = r; }
          else if (r.length > mc + 1) { label = r[0]; nums = r.slice(1, mc + 1); }
          else { nums = [...r, ...Array(mc).fill("0")].slice(0, mc); label = ""; }
          return {name:label, label, values:nums.slice(0,mc).map(toN)};
        });
        setPreview({type:mode, rows:parsed});
      } else {
        if (rows.length === 2 && rows[0].length >= mc) {
          const revRow = rows[0]; const expRow = rows[1];
          const revStart = isNaN(toN(revRow[0])) || isMonthLike(revRow[0]) ? 1 : 0;
          const expStart = isNaN(toN(expRow[0])) || isMonthLike(expRow[0]) ? 1 : 0;
          const dr = months.map((_, i) => ({ rev: toN(revRow[revStart + i] ?? 0), exp: toN(expRow[expStart + i] ?? 0) }));
          setPreview({type:"actuals_col", rows:dr});
        } else {
          const dr = rows.map(r => {
            let cells = r;
            if (r.length >= 3 && isMonthLike(r[0])) { cells = r.slice(1); }
            else if (r.length > 2) { cells = r.slice(-2); }
            if (cells.length < 2) throw new Error("2列必要");
            return {rev:toN(cells[0]), exp:toN(cells[1])};
          });
          if (dr.length < mc) throw new Error(`${mc}行必要（${dr.length}行）`);
          setPreview({type:"actuals_col", rows:dr.slice(0,mc)});
        }
      }
    } catch(e) { setError(e.message); }
  };
  const apply = () => { if(!preview)return; onApply(preview); setText(""); setPreview(null); };
  return (
    <div style={{background:"#F5FAF5",border:"1px dashed #A8D5A8",borderRadius:"8px",padding:"14px",marginBottom:"16px"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:"#2D7A2D",marginBottom:"4px"}}>📋 Excelから一括貼り付け</div>
      <div style={{fontSize:"11px",color:"#666",marginBottom:"8px",fontFamily:"'Noto Sans JP',sans-serif"}}>{hints[mode]}</div>
      <textarea value={text} onChange={e=>{setText(e.target.value);setPreview(null);setError("");}}
        placeholder="ここにExcelのセルをコピー＆ペースト"
        style={{width:"100%",height:"58px",fontSize:"12px",fontFamily:"monospace",border:"1px solid #C8E6C8",borderRadius:"4px",padding:"6px",resize:"vertical",background:"#fff",boxSizing:"border-box"}}
      />
      <div style={{display:"flex",gap:"8px",marginTop:"6px",alignItems:"center"}}>
        <button onClick={parse} disabled={!text.trim()}
          style={{padding:"5px 12px",background:text.trim()?"#2D7A2D":"#ccc",color:"#fff",border:"none",borderRadius:"4px",cursor:text.trim()?"pointer":"default",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif"}}>
          プレビュー
        </button>
        {preview && (
          <button onClick={apply}
            style={{padding:"5px 12px",background:"#1A5C1A",color:"#fff",border:"none",borderRadius:"4px",cursor:"pointer",fontSize:"12px",fontFamily:"'Noto Sans JP',sans-serif",fontWeight:"700"}}>
            ✓ 適用
          </button>
        )}
        {error && <span style={{color:"#C0392B",fontSize:"11px"}}>⚠ {error}</span>}
      </div>
      {preview && mode!=="actuals_col" && (
        <div style={{marginTop:"10px",overflowX:"auto"}}>
          <div style={{fontSize:"10px",color:"#2D7A2D",marginBottom:"4px",fontWeight:"700"}}>プレビュー（適用するまで反映されません）</div>
          <table style={{borderCollapse:"collapse",fontSize:"11px",fontFamily:"monospace"}}>
            <thead><tr>
              <th style={{padding:"2px 5px",background:"#E8F4E8",border:"1px solid #C8E6C8"}}>名前</th>
              {months.map(m=><th key={m} style={{padding:"2px 5px",background:"#E8F4E8",border:"1px solid #C8E6C8"}}>{m}</th>)}
            </tr></thead>
            <tbody>
              {preview.rows.map((r,i)=>(
                <tr key={i}>
                  <td style={{padding:"2px 5px",border:"1px solid #E0ECE0",color:"#2D7A2D",fontWeight:"700"}}>{r.name||`行${i+1}`}</td>
                  {r.values.map((v,j)=><td key={j} style={{padding:"2px 5px",border:"1px solid #E0ECE0",textAlign:"right"}}>{v.toLocaleString()}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function HeaderInfoPills({editorName, savedAt, savedBy, completeCount, partialCount}) {
  if (!savedAt && completeCount === 0 && partialCount === 0) return null;
  return (
    <div style={{display:"flex",gap:"8px",alignItems:"center",padding:"4px 0 8px",maxWidth:"1600px",margin:"0 auto",flexWrap:"wrap"}}>
      {savedAt && <span style={{fontSize:"10px",color:"#aaa",fontFamily:"'Noto Sans JP',sans-serif"}}>最終更新: {savedBy} / {savedAt}</span>}
      {completeCount > 0 && <span style={{fontSize:"10px",padding:"2px 8px",background:"#E8F4E8",color:"#2D7A2D",borderRadius:"999px",fontWeight:"700"}}>{completeCount}ヶ月 入力済み</span>}
      {partialCount > 0  && <span style={{fontSize:"10px",padding:"2px 8px",background:"#FFF3DC",color:"#AA7722",borderRadius:"999px",fontWeight:"700"}}>{partialCount}ヶ月 一部入力</span>}
    </div>
  );
}
